/**
 * Automation Queue
 * AI-powered follow-up email management with bulk preview and approval
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Badge,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Progress,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Input,
  Checkbox,
  Alert,
  AlertIcon,
  AlertDescription,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
} from '@chakra-ui/react';
import {
  MdAutoAwesome,
  MdEmail,
  MdSend,
  MdRefresh,
  MdVisibility,
  MdEdit,
  MdCheckCircle,
  MdSchedule,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  getLeadsNeedingFollowup,
  sendFollowupEmail,
  clearNeedsFollowup,
  setNextFollowupDate,
} from '../../../services/leadManagement';
import { generateFollowupEmail, generateBulkEmails } from '../../../services/aiEmailService';
import type { Lead } from '../../../types/lead';
import type { EmailDraft } from '../../../services/aiEmailService';
import { useAuth } from '../../../contexts/AuthContext';

interface EmailPreview extends EmailDraft {
  lead: Lead;
  selected: boolean;
  edited: boolean;
}

export function AutomationQueue() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [emailPreviews, setEmailPreviews] = useState<EmailPreview[]>([]);
  const [selectedPreview, setSelectedPreview] = useState<EmailPreview | null>(null);
  
  const [bulkGenerateCount, setBulkGenerateCount] = useState<number>(10);
  const [sendingProgress, setSendingProgress] = useState<number>(0);

  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  
  // Edit modal state
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');

  // Load leads needing follow-up
  const loadLeads = async () => {
    try {
      setLoading(true);
      const data = await getLeadsNeedingFollowup();
      setLeads(data);
    } catch (error) {
      toast({
        title: 'Error loading leads',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate single email
  const handleGenerateSingle = async (lead: Lead) => {
    try {
      setGenerating(true);
      const draft = await generateFollowupEmail(lead);
      
      const preview: EmailPreview = {
        ...draft,
        lead,
        selected: false,
        edited: false,
      };
      
      setEmailPreviews(prev => [...prev, preview]);
      
      toast({
        title: 'Email generated',
        description: `Draft ready for ${lead.contactInfo.name}`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  // Generate bulk emails
  const handleGenerateBulk = async () => {
    try {
      setGenerating(true);
      const leadsToGenerate = leads.slice(0, bulkGenerateCount);
      
      const drafts = await generateBulkEmails(leadsToGenerate);
      
      const previews: EmailPreview[] = drafts.map(draft => ({
        ...draft,
        lead: leadsToGenerate.find(l => l.id === draft.leadId)!,
        selected: true, // Pre-select for bulk send
        edited: false,
      }));
      
      setEmailPreviews(previews);
      
      toast({
        title: 'Bulk generation complete',
        description: `Generated ${drafts.length} email drafts`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Bulk generation failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  // Preview email
  const handlePreview = (preview: EmailPreview) => {
    setSelectedPreview(preview);
    onPreviewOpen();
  };

  // Edit email
  const handleEditOpen = (preview: EmailPreview) => {
    setSelectedPreview(preview);
    setEditSubject(preview.subject);
    setEditBody(preview.body);
    onEditOpen();
  };

  // Save edited email
  const handleEditSave = () => {
    if (!selectedPreview) return;

    setEmailPreviews(prev =>
      prev.map(p =>
        p.leadId === selectedPreview.leadId
          ? {
              ...p,
              subject: editSubject,
              body: editBody,
              edited: true,
            }
          : p
      )
    );

    toast({
      title: 'Email updated',
      status: 'success',
      duration: 2000,
    });

    onEditClose();
  };

  // Toggle selection
  const handleToggleSelect = (leadId: string) => {
    setEmailPreviews(prev =>
      prev.map(p => (p.leadId === leadId ? { ...p, selected: !p.selected } : p))
    );
  };

  // Select all / deselect all
  const handleToggleSelectAll = () => {
    const allSelected = emailPreviews.every(p => p.selected);
    setEmailPreviews(prev => prev.map(p => ({ ...p, selected: !allSelected })));
  };

  // Send selected emails
  const handleSendSelected = async () => {
    if (!user) return;

    const selected = emailPreviews.filter(p => p.selected);
    
    if (selected.length === 0) {
      toast({
        title: 'No emails selected',
        description: 'Please select at least one email to send',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setSending(true);
      setSendingProgress(0);

      let sent = 0;
      const total = selected.length;

      for (const preview of selected) {
        try {
          await sendFollowupEmail(
            preview.leadId,
            preview.subject,
            preview.body,
            user.uid,
            user.displayName || 'User',
            !preview.edited // Only mark as AI-generated if not edited
          );

          sent++;
          setSendingProgress(Math.round((sent / total) * 100));
        } catch (error) {
          console.error(`Failed to send email to ${preview.leadName}:`, error);
        }
      }

      toast({
        title: 'Emails sent',
        description: `Successfully sent ${sent} of ${total} emails`,
        status: sent === total ? 'success' : 'warning',
        duration: 5000,
      });

      // Remove sent emails from previews
      setEmailPreviews(prev => prev.filter(p => !p.selected));
      
      // Reload leads
      await loadLeads();
    } catch (error) {
      toast({
        title: 'Send failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSending(false);
      setSendingProgress(0);
    }
  };

  // Skip lead (clear needs follow-up)
  const handleSkipLead = async (leadId: string) => {
    try {
      await clearNeedsFollowup(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      setEmailPreviews(prev => prev.filter(p => p.leadId !== leadId));
      
      toast({
        title: 'Lead skipped',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to skip',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Snooze lead (set next follow-up date)
  // @ts-ignore - Reserved for future use
  const _handleSnoozeLead = async (leadId: string, days: number) => {
    try {
      await setNextFollowupDate(leadId, days);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      
      toast({
        title: `Snoozed for ${days} days`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to snooze',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const selectedCount = emailPreviews.filter(p => p.selected).length;
  const generatedCount = emailPreviews.length;
  const pendingCount = leads.length - generatedCount;

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Icon as={MdAutoAwesome} boxSize={8} color="blue.500" />
          <VStack align="start" spacing={0}>
            <Heading size="lg">Automation Queue</Heading>
            <Text color="gray.600">AI-powered follow-up emails</Text>
          </VStack>
        </HStack>
        <HStack spacing={4}>
          <Button
            leftIcon={<Icon as={MdRefresh} />}
            onClick={loadLeads}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <Grid templateColumns="repeat(4, 1fr)" gap={6} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Leads Pending</StatLabel>
              <StatNumber>{leads.length}</StatNumber>
              <StatHelpText>Need follow-up</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Drafts Generated</StatLabel>
              <StatNumber>{generatedCount}</StatNumber>
              <StatHelpText>{selectedCount} selected</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Ready to Send</StatLabel>
              <StatNumber color="blue.500">{selectedCount}</StatNumber>
              <StatHelpText>
                <Icon as={MdCheckCircle} /> Selected
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Remaining</StatLabel>
              <StatNumber>{pendingCount}</StatNumber>
              <StatHelpText>Not generated</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      {/* Actions */}
      {leads.length > 0 && (
        <Card mb={6}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={4}>
                  <Text fontWeight="bold">Bulk Actions:</Text>
                  <HStack>
                    <Text fontSize="sm">Generate</Text>
                    <Select
                      w="100px"
                      size="sm"
                      value={bulkGenerateCount}
                      onChange={(e) => setBulkGenerateCount(Number(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Select>
                    <Text fontSize="sm">emails</Text>
                  </HStack>
                  <Button
                    leftIcon={<Icon as={MdAutoAwesome} />}
                    colorScheme="purple"
                    size="sm"
                    onClick={handleGenerateBulk}
                    isLoading={generating}
                    isDisabled={leads.length === 0}
                  >
                    Generate with AI
                  </Button>
                </HStack>

                {emailPreviews.length > 0 && (
                  <HStack spacing={4}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleToggleSelectAll}
                    >
                      {emailPreviews.every(p => p.selected) ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      leftIcon={<Icon as={MdSend} />}
                      colorScheme="green"
                      size="sm"
                      onClick={handleSendSelected}
                      isLoading={sending}
                      isDisabled={selectedCount === 0}
                    >
                      Send Selected ({selectedCount})
                    </Button>
                  </HStack>
                )}
              </HStack>

              {sending && (
                <Box>
                  <Text fontSize="sm" mb={2}>Sending emails... {sendingProgress}%</Text>
                  <Progress value={sendingProgress} colorScheme="green" size="sm" />
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Tabs */}
      <Tabs>
        <TabList>
          <Tab>
            <HStack>
              <Icon as={MdEmail} />
              <Text>Email Previews ({emailPreviews.length})</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <Icon as={MdSchedule} />
              <Text>Pending Leads ({pendingCount})</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Email Previews Tab */}
          <TabPanel>
            {emailPreviews.length === 0 ? (
              <Card>
                <CardBody>
                  <VStack spacing={4} py={8}>
                    <Icon as={MdAutoAwesome} boxSize={12} color="gray.300" />
                    <Text color="gray.500">No email drafts generated yet</Text>
                    <Text fontSize="sm" color="gray.400">
                      Use the "Generate with AI" button to create email drafts
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <VStack spacing={4} align="stretch">
                {emailPreviews.map((preview) => (
                  <Card key={preview.leadId} variant="outline">
                    <CardBody>
                      <HStack spacing={4} align="start">
                        <Checkbox
                          isChecked={preview.selected}
                          onChange={() => handleToggleSelect(preview.leadId)}
                          mt={1}
                        />
                        
                        <VStack align="start" flex="1" spacing={2}>
                          <HStack justify="space-between" w="full">
                            <HStack spacing={2}>
                              <Text fontWeight="bold">{preview.leadName}</Text>
                              <Badge colorScheme="blue">{preview.lead.contactInfo.company}</Badge>
                              <Badge colorScheme="purple">{preview.lead.status}</Badge>
                              {preview.edited && (
                                <Badge colorScheme="orange">
                                  <Icon as={MdEdit} mr={1} />
                                  Edited
                                </Badge>
                              )}
                            </HStack>
                            
                            <HStack spacing={2}>
                              <IconButton
                                icon={<MdVisibility />}
                                aria-label="Preview"
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePreview(preview)}
                              />
                              <IconButton
                                icon={<MdEdit />}
                                aria-label="Edit"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditOpen(preview)}
                              />
                              <Button
                                size="sm"
                                colorScheme="green"
                                leftIcon={<MdSend />}
                                onClick={async () => {
                                  if (!user) return;
                                  try {
                                    await sendFollowupEmail(
                                      preview.leadId,
                                      preview.subject,
                                      preview.body,
                                      user.uid,
                                      user.displayName || 'User',
                                      !preview.edited
                                    );
                                    toast({
                                      title: 'Email sent',
                                      status: 'success',
                                      duration: 2000,
                                    });
                                    setEmailPreviews(prev => prev.filter(p => p.leadId !== preview.leadId));
                                    await loadLeads();
                                  } catch (error) {
                                    toast({
                                      title: 'Send failed',
                                      status: 'error',
                                      duration: 3000,
                                    });
                                  }
                                }}
                              >
                                Send Now
                              </Button>
                            </HStack>
                          </HStack>

                          <Box>
                            <Text fontSize="sm" fontWeight="bold" color="gray.700">
                              Subject:
                            </Text>
                            <Text fontSize="sm">{preview.subject}</Text>
                          </Box>

                          <Box>
                            <Text fontSize="sm" fontWeight="bold" color="gray.700">
                              Preview:
                            </Text>
                            <Text fontSize="sm" color="gray.600" noOfLines={3}>
                              {preview.body}
                            </Text>
                          </Box>
                        </VStack>
                      </HStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            )}
          </TabPanel>

          {/* Pending Leads Tab */}
          <TabPanel>
            <Card>
              <CardBody p={0}>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Lead</Th>
                      <Th>Company</Th>
                      <Th>Status</Th>
                      <Th>Doors</Th>
                      <Th>Next Follow-up</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {leads.filter(l => !emailPreviews.find(p => p.leadId === l.id)).map((lead) => (
                      <Tr key={lead.id}>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{lead.contactInfo.name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {lead.contactInfo.email}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>{lead.contactInfo.company || '-'}</Td>
                        <Td>
                          <Badge colorScheme="purple">{lead.status}</Badge>
                        </Td>
                        <Td>{lead.businessInfo.doorCount}</Td>
                        <Td>{formatDate(lead.nextFollowupDate)}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              leftIcon={<MdAutoAwesome />}
                              colorScheme="purple"
                              onClick={() => handleGenerateSingle(lead)}
                              isLoading={generating}
                            >
                              Generate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/admin/sales/leads/${lead.id}`)}
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSkipLead(lead.id)}
                            >
                              Skip
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Email Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPreview && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={1}>To:</Text>
                  <Text>{selectedPreview.leadName} ({selectedPreview.lead.contactInfo.email})</Text>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontWeight="bold" mb={1}>Subject:</Text>
                  <Text>{selectedPreview.subject}</Text>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontWeight="bold" mb={2}>Body:</Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    whiteSpace="pre-wrap"
                    fontFamily="monospace"
                    fontSize="sm"
                  >
                    {selectedPreview.body}
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPreviewClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onPreviewClose();
                if (selectedPreview) {
                  handleEditOpen(selectedPreview);
                }
              }}
            >
              Edit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Editing the email will mark it as manually customized (not AI-generated)
                </AlertDescription>
              </Alert>
              
              <Box>
                <Text fontWeight="bold" mb={2}>Subject:</Text>
                <Input
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                />
              </Box>
              
              <Box>
                <Text fontWeight="bold" mb={2}>Body:</Text>
                <Textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={15}
                  fontFamily="monospace"
                  fontSize="sm"
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleEditSave}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
